import React, { forwardRef } from "react"
import styled, { css } from "styled-components"

import WithForcedMonospace from "./WithForcedMonospace"

import TooltipWrapper, { tooltipStyles } from "./TooltipWrapper"

import { ZERO_WIDTH_SPACE } from "../unicodeChars"

import { useStore } from '../zustandStore'

const DetailedBoardHeaders = forwardRef(({
  children,
  chapterNames,
  includeTotal = true,
  className,
  onClickColumnHeader,
  onDoubleClickColumnHeader,
  onClickRevertSorting = () => {},
  onMouseEnter,
  onMouseLeave = () => {},
  onMouseEnterColumn = () => {},
  sortingByChapterName,
  playerColumnDisabled = false,
  requireDoubleClick,
  onClickNameColumn,
  nameColumnEmptyHoverText,
  stagedChapterName,
  outlineStagedChapter,
  disableTotal,
  totalHoverable,
  totalSelected,
  nameColumnWidthPercent = 17.5,
  includeTooltips = false
}, ref) => {
  const {
    event, chapters
  } = useStore(state => ({
    event: state.event,
    chapters: state.event.chapters
  }))

  if (chapterNames == null) {
    chapterNames = event.chapterNames
  }

  const onMouseEnterLeaveWrapperProps = { onMouseLeave: onMouseLeave }
  if (onMouseEnter) {
    onMouseEnterLeaveWrapperProps['onMouseEnter'] = onMouseEnter
  }

  // only pass along onMouseEnterColumn if one for the wrapper is not supplied
  const onMouseEnterNullColumnProps = {}
  if (!onMouseEnter) {
    onMouseEnterNullColumnProps['onMouseEnter'] = () => onMouseEnterColumn()
  }

  let onClickNullColumnProps = disableTotal
    ? null
    : onClickColumnHeader && onDoubleClickColumnHeader
      ? {onClick: onClickColumnHeader(null), onDoubleClick: onDoubleClickColumnHeader(null)}
      : requireDoubleClick
        ? {onDoubleClick: onClickColumnHeader ? onClickColumnHeader(null) : null}
        : {onClick: onClickColumnHeader ? onClickColumnHeader(null) : null}

  return (
    <HeaderRow ref={ref} {...onMouseEnterLeaveWrapperProps} className={className} nameColumnWidthPercent={nameColumnWidthPercent} chapterCount={chapterNames.length} includeTotal={includeTotal}>
      <PlayerColumnSpacer
        emptyHoverText={nameColumnEmptyHoverText}
        onClick={onClickNameColumn || onClickRevertSorting}
        disabled={playerColumnDisabled}
        className={`column-player ${children ? '' : 'empty'} ${sortingByChapterName || onClickNameColumn ? 'column-hoverable' : ''} ${onClickNameColumn ? 'column-toggle' : ''}`}
        {...onMouseEnterNullColumnProps}
      >
        {/* wrapper span is just for consistency with <WithForcedMonospace> dom structure, for CSS ease */}
        <span className='header-title column-player-children'>{children || ZERO_WIDTH_SPACE}</span>
      </PlayerColumnSpacer>
      {chapterNames.map((chapterName, i) => {
        // only pass along onMouseEnterColumn if one for the wrapper is not supplied
        const onMouseEnterColumnProps = {}
        if (!onMouseEnter) {
          onMouseEnterColumnProps['onMouseEnter'] = () => onMouseEnterColumn(chapterName)
        }

        let clickHandlerProps =
          onClickColumnHeader && onDoubleClickColumnHeader
            ? {onClick: onClickColumnHeader(chapterName), onDoubleClick: onDoubleClickColumnHeader(chapterName)}
            : requireDoubleClick
              ? {onDoubleClick: onClickColumnHeader && onClickColumnHeader(chapterName)}
              : {onClick: onClickColumnHeader && onClickColumnHeader(chapterName)}

        const chapterHeaderOverride = chapters[chapterName].nameOverrides.columnHeader
        const chapterTooltipOverride = chapters[chapterName].nameOverrides.headerTooltip
        const chapterFullName = chapters[chapterName].fullName
        
        return (
          includeTooltips ? (
            <TooltipWrapper
              key={chapterName}
              placement="top"
              // visible={chapterName == 'fp'}
              // visible
              content={chapterTooltipOverride || chapterFullName}
              containerId={`${chapterName}-with-tooltip`}
            >
              <ChapterColumnHeader
                id={`${chapterName}-with-tooltip`}
                key={chapterName}
                isAbbreviated={!chapterHeaderOverride}
                col={i+2}
                className={`column column-hoverable ${sortingByChapterName === chapterName ? 'column-sorting' : ''} ${outlineStagedChapter && stagedChapterName === chapterName ? 'column-staged' : ''}`}
                {...clickHandlerProps}
                {...onMouseEnterColumnProps}
              >
                {chapterHeaderOverride
                  ? <span>{chapterHeaderOverride}</span>
                  : <WithForcedMonospace>{chapterName}</WithForcedMonospace>
                }
              </ChapterColumnHeader>
            </TooltipWrapper>
          ) : (
            <ChapterColumnHeader
              id={`${chapterName}-with-tooltip`}
              key={chapterName}
              isAbbreviated={!chapterHeaderOverride}
              col={i + 2}
              className={`column column-hoverable ${sortingByChapterName === chapterName ? 'column-sorting' : ''} ${outlineStagedChapter && stagedChapterName === chapterName ? 'column-staged' : ''}`}
              {...clickHandlerProps}
              {...onMouseEnterColumnProps}
            >
              {chapterHeaderOverride
                ? <span>{chapterHeaderOverride}</span>
                : <WithForcedMonospace>{chapterName}</WithForcedMonospace>
              }
            </ChapterColumnHeader>
          )
        )
      })}
      {includeTotal &&
        <TotalColumnHeader
          onClick={onClickRevertSorting}
          col={chapterNames.length + 2}
          className={`column column-total ${sortingByChapterName || totalHoverable ? 'column-hoverable' : ''} ${totalSelected ? 'column-sorting' : ''} ${outlineStagedChapter && stagedChapterName === null ? 'column-staged' : ''} ${disableTotal ? 'disabled' : ''}`}
          {...onMouseEnterNullColumnProps}
          {...onClickNullColumnProps}
        >
          {/* wrapper span is just for consistency with <WithForcedMonospace> dom structure, for CSS ease */}
          <span>Total</span>
        </TotalColumnHeader>
      }
    </HeaderRow>
  )
})

// Width of the columns is determined entirely by the parent rendering component
// which is why the 'column' and 'column-player' CSS classes are on some components above
// and also why the styled-components CSS blocks below don't mention width

export default DetailedBoardHeaders

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.nameColumnWidthPercent}% repeat(${props => props.chapterCount}, 10fr) ${props => props.includeTotal && '10fr'};

  font-weight: 500;

  .column, .column-player, .column-total {
    margin: 0;
    padding-top: 5px;
    padding-bottom: 6px;
    /* flex-grow: 1; */
    grid-column: ${props => props.col};
    span, div.monospaced-char {
      border-bottom: 0.08em solid #0000;
      border-top: 0.08em solid #0000;
    }

    &.column-sorting {
      background: var(--header-selected-color);
    }
  }

  .column-sorting {
    span, div.monospaced-char {
      border-bottom: 0.08em solid currentColor;
    }
  }

  .column-hoverable {
    cursor: pointer;
    &:hover {
      @media (hover: hover) {
        background: var(--header-hover-color);
      }
    }
    &:active {
      background: var(--header-active-color);
    }
  }

  .column-player {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-grow: 2;
    &.column-hoverable.empty:hover:after {
      width: 100%;
      line-height: 1.29em;
      font-size: 0.6em;
      ${props => props.emptyHoverText && css`
        content: ${props.emptyHoverText};
      `}
    }
  }

  .column-total.disabled {
    background-color: var(--greyed-out-button-bg);
    color: var(--greyed-out-button-text);
    border: 0px;
    margin: 0px;
    pointer-events: none;
  }

  // this imports the styling from the TooltipWrapper, surely there is a better way
  ${props => tooltipStyles} 
`

const PlayerColumnSpacer = styled.div`
  grid-column: 1;
  white-space: nowrap;
  line-height: 1.29em;
  display: flex;
  align-items: flex-end;
  ${props => css`
    ${props.disabled ? 'pointer-events: none;' : ''}
    ${props => props.emptyHoverText && css`
      align-items: center;
      text-align: center;
      &.column-hoverable.empty:hover:after {
        width: 100%;
        line-height: 1.29em;
        font-size: 0.6em;
        content: '${(props) => props.emptyHoverText}';
      }
    `}
  `}
`

const ColumnHeader = styled.div`
  line-height: 1.29em;
  display: flex;
  justify-content: center;

  grid-column: ${props => props.col};
`

const ChapterColumnHeader = styled(ColumnHeader)`
  ${props => props.isAbbreviated && css`
    text-transform: uppercase;
  `}
  white-space: nowrap;
  .monospaced-char {
    align-items: center;
  }
`

const TotalColumnHeader = styled(ColumnHeader)`
  color: var(--total-color);
`
